import * as React from "react";
import { useNavigate } from "@opendash/router";
import styled from "styled-components";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";
import {
  ContentContainer,
  Card,
  InfoBox,
  SourcesSection,
  TabContainer,
  TabButton,
} from "./common/StyledComponents";

// Route-specific styled components
const WarningBox = styled.div`
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border-left: 4px solid #ff9800;
  padding: 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;

  h4 {
    color: #e65100;
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 8px;
  }
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-left: 4px solid #4caf50;
  padding: 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;

  &::before {
    content: "FAZIT";
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #2e7d32;
    opacity: 0.6;
    margin-bottom: 8px;
  }

  h4 {
    color: #2e7d32;
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 8px;
  }
`;

export const KlimawandelRoute = React.memo(function KlimawandelRoute() {
  const navigate = useNavigate();
  const { t } = useTexts();

  const tabs = [
    {
      title: t("regenwasser.tab.grauwasser", "Regen- und Grauwasser"),
      path: "/wasserklaerer/regenwasser",
      isActive: true,
    },
    {
      title: t("regenwasser.tab.wasserzaehler", "Wasserzähler"),
      path: "/wasserklaerer/regenwasser/wasserzaehler",
      isActive: false,
    },
    {
      title: t("regenwasser.tab.leitungen", "Leitungen"),
      path: "/wasserklaerer/regenwasser/leitungen",
      isActive: false,
    },
  ];

  return (
    <AppLayout>
      <ContentContainer>
        <h1>{t("regenwasser.title", "Regen- und Grauwassernutzung")}</h1>

        <TabContainer>
          {tabs.map((tab, index) => (
            <TabButton
              key={index}
              isActive={tab.isActive}
              onClick={() => !tab.isActive && navigate(tab.path)}
            >
              {tab.title}
            </TabButton>
          ))}
        </TabContainer>

        <Card>
          <h2>{t("regenwasser.section1.title", "Worum geht es?")}</h2>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section1.text1",
                "Im Kapitel \u201eWasserknappheit\u201c erfahren Sie, dass unsere wichtigste Ressource Wasser nicht unbegrenzt ist, und wir sorgsam damit umgehen sollten. Hierbei kann die Verwertung von Regen- oder Grauwasser einen Beitrag f\u00fcr eine nachhaltige Wasserverwendung leisten. [1]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section1.text2",
                "Denn h\u00e4ufig k\u00f6nnen kostenintensive, unterirdisch verlegte Kanalsysteme sowie Regenw\u00fcber\u00e4ufe entbehrlich werden, wenn sie durch eine wirtschaftliche Form der Regenwasserbewirtschaftung ersetzt werden. Gleichzeitig wird der Wasserkreislauf kleinr\u00e4umiger geschlossen, wodurch der technische Aufwand und der Energiebedarf f\u00fcr die Reinigung und Aufbereitung des Wassers in Kl\u00e4ranlagen und Wasserwerken entfallen. [2]"
              )
            )}
          </p>
        </Card>

        <Card>
          <h2>
            {t(
              "regenwasser.section2.title",
              "Die einfachste Methode zur Verwertung von Regenwasser"
            )}
          </h2>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section2.text1",
                "Regenwasser in Regentonnen zu sammeln und zur Gartenbewässerung zu nutzen ist wohl die einfachste Methode, um einen Beitrag zur Nachhaltigkeit zu leisten. [1] [2] Hinzu kommt, dass Pflanzen im Garten das weiche Regenwasser im Regelfall auch besser vertragen als das härtere Leitungswasser. [2]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section2.text2",
                "Regenwasser sinnvoll zu nutzen kann auch sein dieses einfach in den Boden versickern zu lassen. Denn Regenwasser füllt unsere Grundwasserspeicher regelmäßig auf. Durch das Wachstum von Städten in den letzten Jahrhunderten treten Flächenversiegelungen immer häufiger auf, die eine Versickerung verhindern. Sobald Sie den Durchfluss von Regenwasser in den Untergrund gezielt fördern, leisten Sie bereits einen Beitrag zur Stärkung des lokalen Wasserhaushalts. Hierbei sollten Sie beachten, dass Böden mit hohem Tonanteil nur bedingt für eine Versickerung geeignet sind, da hier eine Staugefahr besteht. [1] [2]"
              )
            )}
          </p>
        </Card>

        <Card>
          <h2>
            {t(
              "regenwasser.section3.title",
              "Warum Sie auf die Beschaffung Ihres Dachs achten sollten"
            )}
          </h2>
          <p>
            {t(
              "regenwasser.section3.text1",
              "Beim Abfluss von Regenwasser über Kupfer- oder Zinkdächer können sich metallische Verbindungen lösen und in das Dachablaufwasser übergehen. Dieses belastete Wasser ist für eine Nutzung im Garten nicht geeignet. Sind hingegen lediglich Dachrinnen und Fallrohre aus Kupfer oder Zink gefertigt, gilt die Verwendung des gesammelten Regenwassers in der Regel als unbedenklich. Dachflächen aus Aluminium oder Edelstahl stellen ebenfalls kein Risiko dar."
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section3.text2",
                "Gleiches gilt jedoch nicht für Dächer, die mit Bitumenbahnen abgedichtet sind, da diese Herbizide enthalten können; auch in diesem Fall wird von einer gärtnerischen Nutzung des Dachablaufwassers abgeraten. [2]"
              )
            )}
          </p>
          <InfoBox>
            <h4>{t("regenwasser.section3.tip.title", "Tipp")}</h4>
            <p>
              {processInlineFormatting(
                t(
                  "regenwasser.section3.tip.text",
                  "Eine umweltverträgliche Alternative besteht insbesondere bei Neubedachungen in der Auswahl von Dachmaterialien mit dem Umweltzeichen \u201eBlauer Engel\u201c. Diese Produkte basieren auf unbedenklichen Rezepturen und erfüllen strenge Anforderungen hinsichtlich ihrer ökotoxikologischen Wirkungen. [3]"
                )
              )}
            </p>
          </InfoBox>
        </Card>

        <Card>
          <h2>
            {t("regenwasser.section4.title", "Regenwassernutzung im Haushalt")}
          </h2>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section4.text1",
                "Regenwasser kann grundsätzlich auch im Haushalt genutzt werden, setzt jedoch eine vorherige Aufbereitung voraus. Mithilfe geeigneter Anlagen müssen potenziell gesundheits- und umweltschädliche Stoffe entfernt werden, um hygienische Risiken zu minimieren. Zusätzlich werden Schwebstoffe eliminiert, die aus technischen Gründen eine weitere Verwendung beeinträchtigen könnten. Aufbereitetes Regenwasser eignet sich anschließend beispielsweise für die Toilettenspülung oder für den Einsatz in der Waschmaschine. [1]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section4.text2",
                "Die hierfür verfügbaren technischen Verfahren und Produkte gelten als ausgereift und sind in einem entsprechenden technischen Regelwerk verankert. Darüber hinaus ist die Nutzung rechtlich durch die Trinkwasserverordnung geregelt. Diese schreibt unter anderem vor, dass Leitungen für aufbereitetes Regenwasser strikt von Trinkwasserleitungen zu trennen sind. Eine Vermischung wird durch geeignete Sicherungseinrichtungen verhindert, zudem müssen die Leitungen eindeutig, etwa durch farbliche Kennzeichnung, unterscheidbar sein. Die Installation solcher Anlagen darf ausschließlich durch fachkundiges Personal erfolgen. [2] [4]"
              )
            )}
          </p>
          <WarningBox>
            <h4>{t("regenwasser.section4.warning.title", "Wichtig")}</h4>
            <p>
              {processInlineFormatting(
                t(
                  "regenwasser.section4.warning.text",
                  "Diese strikte Trennung der Kanäle liegt darin begründet, dass Regenwasser trotz der Aufbereitungsanlage keine Trinkwasserqualität erreicht. Es entspricht vielmehr den Anforderungen an Badegewässer. Ein Konsum von aufbereitetem Regenwasser ist daher mit hygienischen Risiken verbunden und nicht zulässig. [2]"
                )
              )}
            </p>
          </WarningBox>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section4.text3",
                "Regenwassernutzungsanlagen im Haushalt erfordern eine regelmäßige Pflege. Die Verantwortung dafür liegt bei den Betreiberinnen und Betreibern. Entscheidend ist, dass die Anlage regelmäßig gewartet und hygienisch überprüft wird. Auch nach mehrjährigem Betrieb muss die Regenwassernutzungsanlage dauerhaft hygienisch einwandfrei funktionieren. [2]"
              )
            )}
          </p>
        </Card>

        <Card>
          <h2>
            {t(
              "regenwasser.section5.title",
              "Aufpassen beim Wäschewaschen mit Regenwasser"
            )}
          </h2>
          <p>
            {t(
              "regenwasser.section5.text1",
              "Durch Wäschewaschen mit weichem Regenwasser können rund 20 Prozent Waschpulver eingespart werden. Diesem ökonomischen und ökologischen Vorteil stehen jedoch insbesondere für gesundheitlich vulnerable Personengruppen hygienische Bedenken gegenüber."
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section5.text2",
                "Zwar werden potenziell gesundheitsgefährdende Keime während des Waschvorgangs in der Regel durch den Einsatz von Waschmitteln und erhöhte Temperaturen weitgehend inaktiviert. Bei den nachfolgenden Spülgängen mit kaltem Wasser kann dies jedoch nicht zuverlässig gewährleistet werden, sodass die Übertragung von Keimen auf die Textilien nicht ausgeschlossen ist. [2]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section5.text3",
                "Dieses Restrisiko lässt sich lediglich durch eine gründliche Aufbereitung des Regenwassers oder durch eine nachträgliche thermische Behandlung der Wäsche, etwa durch Bügeln, minimieren. Bei der Nutzung von Regenwasser zum Wäschewaschen ist dieser Umstand daher zwingend zu berücksichtigen. [2]"
              )
            )}
          </p>
        </Card>

        <Card>
          <h2>
            {t(
              "regenwasser.section6.title",
              "Kosten einer Regenwassernutzungsanlage"
            )}
          </h2>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section6.intro",
                "Die Nutzung von Regenwasser kann langfristig Kosten senken, ist jedoch zunächst mit hohen Investitionen verbunden. [2]"
              )
            )}
          </p>
          <InfoBox>
            <h4>{t("regenwasser.section6.costs.title", "Kostenübersicht")}</h4>
            <ul>
              <li>
                {processInlineFormatting(
                  t(
                    "regenwasser.section6.costs.item1",
                    "Die Anschaffungs- und Wartungskosten einer Regenwassernutzungsanlage liegen bei fachgerechter Installation im Bereich von etwa 2.500 bis 5.000 Euro [2]"
                  )
                )}
              </li>
              <li>
                {processInlineFormatting(
                  t(
                    "regenwasser.section6.costs.item2",
                    "Ein Vier-Personen-Haushalt kann durch die Nutzung von Regenwasser jährlich rund 40 Kubikmeter Trinkwasser einsparen. Wird Regenwasser zusätzlich zum Wäschewaschen eingesetzt, erhöht sich die Einsparung auf etwa 60 Kubikmeter. Daraus ergibt sich eine jährliche Kostenersparnis von ungefähr 160 bis 200 Euro. [2]"
                  )
                )}
              </li>
              <li>
                {processInlineFormatting(
                  t(
                    "regenwasser.section6.costs.item3",
                    "Demgegenüber stehen laufende Kosten. Bei externer Wartung fallen etwa 100 Euro pro Jahr an. In einigen Bundesländern kommen zusätzliche Ausgaben für separate Wasserzähler und deren regelmäßige Eichung hinzu. [2]"
                  )
                )}
              </li>
            </ul>
          </InfoBox>
        </Card>

        <Card>
          <h2>
            {t("regenwasser.section7.title", "Was ist Grauwasser eigentlich?")}
          </h2>
          <p>
            {t(
              "regenwasser.section7.text1",
              "Abgesehen vom Regenwasser kann auch Grauwasser verwertet werden für die Nutzung im Haushalt. Doch was ist Grauwasser eigentlich?"
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section7.text2",
                "Als \u201eGrauwasser\u201c wird das im Haushalt anfallende Abwasser aus Duschen, Baden sowie dem Hände- und Wäschewaschen bezeichnet. Laut Fachvereinigung Betriebs- und Regenwassernutzung fallen in einem wassersparenden Haushalt täglich 55 Liter Grauwasser pro Person an. [5] Es ist nur gering verschmutzt und kann daher relativ einfach aufbereitet werden."
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section7.text3",
                "Küchenabwasser und Toilettenwasser weisen hingegen einen höheren Verschmutzungsgrad auf und werden daher gesondert als \u201eSchwarzwasser\u201c eingestuft. Dieses kann nicht mit Grauwassernutzungsanlagen behandelt werden, sondern wird direkt in die Kanalisation geleitet und in Kläranlagen gereinigt. [4] [5]"
              )
            )}
          </p>
          <InfoBox>
            <h4>{t("regenwasser.section7.tip.title", "Energieeffizienz")}</h4>
            <p>
              {processInlineFormatting(
                t(
                  "regenwasser.section7.tip.text",
                  "Da Grauwasser überwiegend warm anfällt, ist der Einsatz einer zusätzlichen Wärmerückgewinnung in vielen Fällen sinnvoll. Dabei wird die im Grauwasser enthaltene Wärme auf frisches Trinkwasser übertragen und zwischengespeichert, was den Energiebedarf weiter reduzieren kann. [4]"
                )
              )}
            </p>
          </InfoBox>
        </Card>

        <Card>
          <h2>
            {t(
              "regenwasser.section8.title",
              "Wie funktioniert eine Grauwasseranlage?"
            )}
          </h2>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section8.text1",
                "Nach dem Duschen, Baden oder Händewaschen fließt das Grauwasser zunächst wie gewohnt in den Abfluss. Anschließend wird es jedoch über eine separate Leitung zu einer Grauwassernutzungsanlage geführt. Diese besteht in der Regel aus zwei Speichern: einem Grauwasserspeicher und einem Betriebswasserspeicher. Bevor das Wasser gespeichert wird, erfolgt eine erste grobe Reinigung durch einen Filter. [4]"
              )
            )}
          </p>
          <h3>
            {t("regenwasser.section8.subtitle", "Aufbereitungsverfahren")}
          </h3>
          <p>
            {t(
              "regenwasser.section8.text2",
              "Daran schließt sich eine Feinaufbereitung an, bei der unterschiedliche Verfahren eingesetzt werden:"
            )}
          </p>
          <ul>
            <li>
              {t(
                "regenwasser.section8.method1",
                "Biologische Aufbereitung mit Hilfe von Mikroorganismen"
              )}
            </li>
            <li>
              {t(
                "regenwasser.section8.method2",
                "Physikalische Reinigung durch extrem feine Membranfilter"
              )}
            </li>
            <li>
              {t(
                "regenwasser.section8.method3",
                "UV-Bestrahlung als Alternative zum Membranfilter"
              )}
            </li>
          </ul>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section8.text3",
                "Bei der biologischen Aufbereitung bauen Mikroorganismen organische Bestandteile wie Seifenreste, Haare oder Öle ab. Ergänzend oder alternativ kommen physikalische Membranfilter zum Einsatz, die aufgrund ihrer sehr feinen Poren Bakterien und Viren zurückhalten. Eine weitere Möglichkeit ist die Desinfektion mittels UV-Bestrahlung, bei der Mikroorganismen unschädlich gemacht werden. [4]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section8.text4",
                "Nach der Aufbereitung wird das Wasser im Betriebswasserspeicher gesammelt und für die weitere Nutzung bereitgestellt. Es kann wie bei der Regenwassernutzungsanlage für die Toilettenspülung verwendet oder an ausgewählte Wasseranschlüsse im Haushalt angeschlossen werden. In der Regel erreicht das Betriebswasser die Qualitätsanforderungen der EU für Badegewässer. Entscheidend ist jedoch die Leistungsfähigkeit der Anlage. Einfach ausgeführte Systeme reinigen das Grauwasser oft nur eingeschränkt, sodass eine Nutzung meist auf die Toilettenspülung oder die Gartenbewässerung begrenzt bleibt. [4]"
              )
            )}
          </p>
        </Card>

        <Card>
          <h2>
            {t(
              "regenwasser.section9.title",
              "Installation und Kosten einer Grauwasseranlage"
            )}
          </h2>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section9.text1",
                "Ebenso wie bei der Regenwassernutzungsanlage müssen bei der Grauwassernutzungsanlage alle Leitungen gesondert gekennzeichnet sein. Somit wird eine strikte Trennung zwischen Trink- und aufbereitetem Grauwasser gewährleistet. Aufgrund des hohen Aufwands macht es meistens nur Sinn eine Grauwasseranlage zu montieren, wenn sowieso eine Grundsanierung der Sanitäranlagen geplant ist. [4]"
              )
            )}
          </p>
          <InfoBox>
            <h4>
              {t(
                "regenwasser.section9.costs.title",
                "Kostenübersicht Grauwasser"
              )}
            </h4>
            <ul>
              <li>
                {processInlineFormatting(
                  t(
                    "regenwasser.section9.costs.item1",
                    "Kosten einer Grauwasseranlage: 5.000 Euro [4]"
                  )
                )}
              </li>
              <li>
                {processInlineFormatting(
                  t(
                    "regenwasser.section9.costs.item2",
                    "Installationskosten: mindestens 500 Euro [4]"
                  )
                )}
              </li>
              <li>
                {processInlineFormatting(
                  t(
                    "regenwasser.section9.costs.item3",
                    "Einsparung: 500 Euro pro Jahr (Vier-Personenhaushalt) [4]"
                  )
                )}
              </li>
              <li>
                {processInlineFormatting(
                  t(
                    "regenwasser.section9.costs.item4",
                    "Hinzu kommen noch laufende Kosten für Betrieb und Wartung [4]"
                  )
                )}
              </li>
            </ul>
          </InfoBox>
        </Card>

        <HighlightBox>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section10.text1",
                "Der ökologische und ökonomische Nutzen von Regenwasser- und Grauwassernutzungsanlagen in privaten Haushalten ist in vielen Fällen gering oder nicht eindeutig nachweisbar. Insbesondere die langen Amortisationszeiten machen beide Systeme für den privaten Einsatz meist wirtschaftlich unattraktiv. [2] [4]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section10.text2",
                "Im Einzelfall kann dennoch geprüft werden, ob eine Regenwassernutzungsanlage wirtschaftlich und ökologisch sinnvoll ist. Die Erfolgsaussichten steigen mit dem Wasserverbrauch des Haushalts. Somit eignen sich Grauwasseranlagen vor allem für Gebäude mit hohem und konstantem Wasserbedarf, etwa Hotels, größere Wohnanlagen oder Campingplätze. [2] [4] [5]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "regenwasser.section10.text3",
                "Unabhängig von der Wirtschaftlichkeit bieten beide Systeme ökologische Vorteile. Sie schonen Grundwasserressourcen, reduzieren den Einsatz von Energie und Chemikalien bei der Trinkwasserbereitstellung und entlasten Kläranlagen sowie Gewässer nachhaltig. [4]"
              )
            )}
          </p>
        </HighlightBox>

        <SourcesSection>
          <h3>{t("regenwasser.literatur.title", "Literatur")}</h3>
          <ol>
            <li id="quelle-1">
              <LinkifyText>
                {t(
                  "regenwasser.literatur.ref1",
                  "C. Baumgarten, J. Rechenberg, S. Richter, I. Chorus, L. Vigelahn, und O. Schmoll, 'Wassersparen in Privathaushalten: sinnvoll, ausgereizt, übertrieben? - Fakten, Hintergründe, Empfehlungen', Umweltbundesamt, 2014. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/sites/default/files/medien/479/publikationen/hgp_wassersparen_in_privathaushalten_web.pdf"
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-2">
              <LinkifyText>
                {t(
                  "regenwasser.literatur.ref2",
                  "Umweltbundesamt (Hrsg.), 'Tipps für eine nachhaltige Regenwassernutzung', Umweltbundesamt, 2025. Zugegriffen: 15. Dezember 2025. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/umwelttipps-fuer-den-alltag/garten-freizeit/regenwassernutzung"
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-3">
              <LinkifyText>
                {t(
                  "regenwasser.literatur.ref3",
                  "RAL (Hrsg.) und Umweltbundesamt (Hrsg.), 'Dach- und Dichtungsbahnen (DE-UZ 224)', blauer-engel. Zugegriffen: 17. Dezember 2025. [Online]. Verfügbar unter: https://www.blauer-engel.de/de/produktwelt/dach-und-dichtungsbahnen"
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-4">
              <LinkifyText>
                {t(
                  "regenwasser.literatur.ref4",
                  "D. Hochwarth, 'Grauwassernutzung: Lohnt sich Wasserrecycling zuhause?', ingenieur.de, 2022. Zugegriffen: 15. Dezember 2025. [Online]. Verfügbar unter: https://www.ingenieur.de/technik/fachbereiche/umwelt/grauwassernutzung-so-funktioniert-wasserrecycling-zuhause/"
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-5">
              <LinkifyText>
                {t(
                  "regenwasser.literatur.ref5",
                  "M. D. Nguyen, 'Grauwasser: Defintion, Recycling & Sparpotenzial', co2online, 2024. Zugegriffen: 15. Dezember 2025. [Online]. Verfügbar unter: https://www.co2online.de/energie-sparen/wasser-sparen/wasserkreislauf/grauwasser/"
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
          </ol>
        </SourcesSection>
      </ContentContainer>
    </AppLayout>
  );
});
